import {
  toEmployeeCoreDTO,
  toEmployeePayrollDTO,
  toEmployeeDetailsDTO,
  toEmployeeAttendanceDTO,
  toEmployeeNonOrderRelatedTimeDTO,
  fromEmployeeCoreDTO,
  fromEmployeePayrollDTO,
  fromEmployeeDetailsDTO,
  fromEmployeeAttendanceDTO,
  fromEmployeeNonOrderRelatedTimeDTO,
} from "../dto/employeeMapper";

export const EMPLOYEE_PARTS = [
  {
    sourceKey: "employees",
    endpoint: "employee",
    recordIdKey: "employeeId",
    toDTO: (employee, { companyId }) => toEmployeeCoreDTO({ ...employee, companyId, hash: employee.hash }),
    fromDTO: fromEmployeeCoreDTO,
  },
  {
    sourceKey: "employeePayrolls",
    endpoint: "employee-payroll",
    recordIdKey: "employeePayrollId",
    toDTO: (employee, { companyInputId }) => toEmployeePayrollDTO(employee, companyInputId),
    fromDTO: fromEmployeePayrollDTO,
  },
  {
    sourceKey: "employeeEmplDetails",
    endpoint: "employee-empl-details",
    recordIdKey: "employeeEmplDetailId",
    toDTO: (employee, { companyInputId }) => toEmployeeDetailsDTO(employee, companyInputId),
    fromDTO: fromEmployeeDetailsDTO,
  },
  {
    sourceKey: "employeeAttendanceTimes",
    endpoint: "employee-attendance-time",
    recordIdKey: "employeeAttendanceTimeId",
    toDTO: (employee, { companyInputId }) => toEmployeeAttendanceDTO(employee, companyInputId),
    fromDTO: fromEmployeeAttendanceDTO,
  },
  {
    sourceKey: "employeeNonOrderRelatedAttTime",
    endpoint: "employee-non-order-related-att-time",
    recordIdKey: "employeeNonOrderRelatedAttTimeId",
    toDTO: (employee, { companyInputId }) => toEmployeeNonOrderRelatedTimeDTO(employee, companyInputId),
    fromDTO: fromEmployeeNonOrderRelatedTimeDTO,
  },
];

export const EMPLOYEE_MODEL_SCHEMA = Object.freeze({
  nameOrNum: { type: "string" },
  companyHash: { type: "string" },
  hash: { type: "string" },
  id: { type: "string" },
  employeeId: { type: "number" },
  employeePayrollId: { type: "number" },
  employeeAbsencesId: { type: "number" },
  employeeEmplDetailId: { type: "number" },
  employeeAttendanceTimeId: { type: "number" },
  employeeNonOrderRelatedAttTimeId: { type: "number" },
  emplStartDate: { type: "string" },
  emplEndDate: { type: "string" },
  employmentType: { type: "object" },
  employmentGrade: { type: "number" },
  yearOfBirth: { type: "number" },
  sex: { type: "object" },
  specXFunc: { type: "object" },
  showAllSpecXFunc: { type: "boolean" },
  employmentLevel: { type: "object" },
  employmentStatus: { type: "object" },
  nominalAttendanceTime: { type: "number" },
  yearlyPresenceTime: { type: "number" },
  educationTraining: { type: "number" },
  advertisingAcquisition: { type: "number" },
  contest: { type: "number" },
  managementLeadership: { type: "number" },
  edvOperation: { type: "number" },
  others: { type: "number" },
  grossSalary: { type: "number" },
  baseSalary: { type: "number" },
  addBenefits: { type: "number" },
  remarks: { type: "string" },
  errors: { type: "object" },
});

export const OPTION_FIELDS = Object.freeze({
  specXFunc: { idKey: "specXFuncId", listKeys: ["allEmployeeFunctions", "specXFunc"] },
  employmentLevel: { idKey: "employmentLevelId", listKeys: ["employmentLevels", "employmentLevel"] },
  employmentStatus: { idKey: "employmentStatusId", listKeys: ["employmentStatuses", "employmentStatus"] },
  sex: { idKey: "sex", listKeys: ["employeeSexes", "sex"] },
  employmentType: { idKey: "employmentType", listKeys: ["employmentTypes", "employmentType"] },
});

export const PERSISTED_ID_KEYS = new Set(EMPLOYEE_PARTS.map(({ recordIdKey }) => recordIdKey));
export const EMPTY_ID_FIELDS = new Set(["id", "employeeAbsencesId", ...PERSISTED_ID_KEYS]);
export const NUMERIC_FIELDS = new Set([
  "yearOfBirth",
  "grossSalary",
  "addBenefits",
  "baseSalary",
  "employmentGrade",
  "nominalAttendanceTime",
  "yearlyPresenceTime",
  "others",
  "edvOperation",
  "managementLeadership",
  "contest",
  "advertisingAcquisition",
  "educationTraining",
  "employeeAbsencesId",
  ...PERSISTED_ID_KEYS,
]);
export const hasValue = (value) => value !== null && value !== undefined && value !== "";
export const toId = (value) => (typeof value === "object" && value ? value.id : value);
export const sameId = (left, right) => hasValue(left) && hasValue(right) && String(left) === String(right);
export const emptyOption = (value = null) => ({
  id: hasValue(toId(value)) ? toId(value) : null,
  label: "",
  name: "",
});
export const toSafeOption = (value) => {
  if (!hasValue(toId(value))) return emptyOption(value);
  if (typeof value === "object" && value) {
    const label = value.label ?? value.name ?? value.value ?? String(value.id ?? "");
    return { ...value, label, name: value.name ?? label };
  }
  const label = String(value);
  return { id: value, label, name: label };
};
export const getOptionList = (data, keys) => {
  const seen = new Set();
  return keys
    .flatMap((key) => (Array.isArray(data?.[key]) ? data[key] : []))
    .filter((option) => {
      const id = toId(option);
      if (!hasValue(id) || seen.has(String(id))) return false;
      seen.add(String(id));
      return true;
    });
};
export const getDefaultModelValue = (field, type) => {
  if (EMPTY_ID_FIELDS.has(field) || type === "number") return null;
  if (type === "boolean") return false;
  if (type === "object") return emptyOption();
  return "";
};
